// OCR in the UI: shared status, the "read the document & fill the form" strip in the document form,
// the recognised-text viewer (with the places each field was read from) and the drawer's "Text" tab.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { Attribute, ExtractResult, OcrResult, OcrStatus, Suggestion } from '../types';
import { Modal, fmtDateTime, useToast } from '../ui';
import { Icon } from '../ui/Icon';

// ---------------------------------------------------------------- status (cached for the session, refreshed every minute)
let statusCache: { at: number; p: Promise<OcrStatus | null> } | null = null;
export function loadOcrStatus(force = false): Promise<OcrStatus | null> {
  if (!force && statusCache && Date.now() - statusCache.at < 60_000) return statusCache.p;
  const p = api.get<OcrStatus>('/api/ocr/status').catch(() => null); // OCR service absent → feature hidden
  statusCache = { at: Date.now(), p };
  return p;
}
export function useOcrStatus(): OcrStatus | null {
  const [s, setS] = useState<OcrStatus | null>(null);
  useEffect(() => { let alive = true; loadOcrStatus().then(x => { if (alive) setS(x); }); return () => { alive = false; }; }, []);
  return s;
}

const FILLABLE = new Set(['TEXT', 'TEXTAREA', 'DATE', 'LIST', 'NUMBER']);
/** Form fields the OCR can fill (the registration date and department pickers are never guessed). */
export const TITLE_FIELD = { code: '__titlu', name: 'Titlu scurt', type: 'TEXT', allowAdd: false, items: [] as { value: string; label: string }[],
  description: 'titlul documentului în registru: 3–8 cuvinte, esența cererii (ce se solicită și despre ce/unde), fără numele expeditorului, fără cuvinte ca „Petiție”, „Cerere”, „Solicitare” la început; exemplu: „Reparare carosabil și scurgere ape pluviale – Str. Livezii”' };
export function ocrFields(attrs: Attribute[], locked: Record<string, string>, withTitle = false) {
  return [...(withTitle ? [TITLE_FIELD] : []), ...ocrAttrFields(attrs, locked)];
}
function ocrAttrFields(attrs: Attribute[], locked: Record<string, string>) {
  return attrs.filter(a => FILLABLE.has(a.type) && !locked[a.code]).map(a => ({
    code: a.code, name: a.name, type: a.type, allowAdd: !!a.listAllowAdd, description: a.description || '',
    items: (a.items || []).filter(i => i.active !== false).map(i => ({ value: i.value, label: i.label || i.value })),
  }));
}

/** Waits for the OCR of a file (the server holds each request up to 25 s). */
export async function waitOcr(fileId: string, start: boolean, onState?: (r: OcrResult) => void): Promise<OcrResult> {
  let r = start ? await api.post<OcrResult>(`/api/ocr/files/${fileId}`, {}) : await api.get<OcrResult>(`/api/ocr/files/${fileId}?text=false`);
  onState?.(r);
  for (let i = 0; i < 40 && (r.status === 'QUEUED' || r.status === 'RUNNING'); i++) {
    r = await api.get<OcrResult>(`/api/ocr/files/${fileId}?wait=true&text=false`);
    onState?.(r);
  }
  return r;
}

export const SOURCE_LABEL: Record<string, string> = { RULE: 'Regulă', AI: 'Gemma', BOTH: 'Regulă + Gemma' };
export function methodLabel(m?: string | null) { return m === 'pdf-text' ? 'text din PDF (fără OCR)' : m === 'tesseract' ? 'OCR Tesseract' : m === 'text' ? 'fișier text' : m || '—'; }

/** Small badge next to a field label filled from the document. */
export function OcrBadge({ s }: { s: Suggestion }) {
  const title = `${s.confidence === 'CHECK' ? 'De verificat — ' : ''}completat din document (${SOURCE_LABEL[s.source]}${s.ruleName ? ': ' + s.ruleName : ''})${s.snippet ? `\n„${s.snippet}”` : ''}${s.alternative ? `\nAltă variantă (${SOURCE_LABEL[s.alternative.source] || s.alternative.source}): ${s.alternative.display}` : ''}`;
  return <span className={'ocr-badge ' + s.source + (s.confidence === 'CHECK' ? ' check' : '')} title={title}><Icon name={s.source === 'RULE' ? 'scan' : 'sparkles'} size={11} />{s.confidence === 'CHECK' ? 'verificați' : s.source === 'RULE' ? 'OCR' : 'AI'}</span>;
}

// ---------------------------------------------------------------- recognised text with highlighted matches
const COLORS = ['#fde68a', '#bfdbfe', '#bbf7d0', '#fbcfe8', '#ddd6fe', '#fed7aa', '#a5f3fc', '#e9d5ff'];
export function HighlightedText({ text, spans, query }: { text: string; spans?: { start: number; end: number; label: string; color?: string }[]; query?: string }) {
  const parts = useMemo(() => {
    const marks: { start: number; end: number; label: string; color: string }[] = [];
    (spans || []).forEach((s, i) => { if (s.start != null && s.end > s.start) marks.push({ ...s, color: s.color || COLORS[i % COLORS.length] }); });
    const q = (query || '').trim().toLowerCase();
    if (q.length >= 2) { const low = text.toLowerCase(); let at = low.indexOf(q); let n = 0; while (at >= 0 && n++ < 500) { marks.push({ start: at, end: at + q.length, label: 'căutare', color: '#fef08a' }); at = low.indexOf(q, at + q.length); } }
    marks.sort((a, b) => a.start - b.start);
    const out: React.ReactNode[] = []; let pos = 0;
    marks.forEach((m, i) => {
      if (m.start < pos) return;
      if (m.start > pos) out.push(text.slice(pos, m.start));
      out.push(<mark key={i} style={{ background: m.color }} title={m.label}>{text.slice(m.start, m.end)}</mark>);
      pos = m.end;
    });
    out.push(text.slice(pos));
    return out;
  }, [text, spans, query]);
  return <pre className="ocr-text">{parts}</pre>;
}

export function OcrTextModal({ text, suggestions, result, onClose }: { text: string; suggestions?: Suggestion[]; result?: OcrResult | null; onClose: () => void }) {
  const [q, setQ] = useState('');
  const spans = (suggestions || []).filter(s => s.start != null).map((s, i) => ({ start: s.start!, end: s.end!, label: s.field, color: COLORS[i % COLORS.length] }));
  return (
    <Modal title="Textul recunoscut din document" icon="scan" size="lg" onClose={onClose}
      footer={<>{result?.searchableFileId && <a className="btn sm" href={api.fileUrl(result.searchableFileId, 'download')}><Icon name="download" />PDF căutabil</a>}<span className="grow" /><button className="btn" onClick={onClose}>Închide</button></>}>
      <div className="stack" style={{ gap: 10 }}>
        {result && <OcrMeta r={result} />}
        {spans.length > 0 && <div className="ocr-legend">{spans.map((s, i) => <span key={i}><i style={{ background: s.color }} />{s.label}</span>)}</div>}
        <div className="search"><Icon name="search" size={16} /><input className="input" type="search" autoComplete="off" placeholder="Caută în text…" value={q} onChange={e => setQ(e.target.value)} /></div>
        <HighlightedText text={text} spans={spans} query={q} />
      </div>
    </Modal>
  );
}

export function OcrMeta({ r }: { r: OcrResult }) {
  return (
    <div className="ocr-meta">
      <span><Icon name="scan" size={13} />{methodLabel(r.method)}</span>
      {r.pages ? <span>{r.pages} {r.pages === 1 ? 'pagină' : 'pagini'}</span> : null}
      {r.confidence != null && <span title="Încrederea medie a recunoașterii, raportată de Tesseract">încredere {Math.round(r.confidence)}%</span>}
      {r.durationMs != null && <span>{(r.durationMs / 1000).toFixed(1)} s</span>}
      {r.updatedAt && <span>{fmtDateTime(r.updatedAt)}</span>}
    </div>
  );
}

// ---------------------------------------------------------------- the strip in the document form
export interface OcrFillProps {
  status: OcrStatus | null;
  fileId: string | null;            // attached file
  isNewFile: boolean;               // just uploaded in this form (→ automatic run)
  typeId?: number; direction: 'IN' | 'OUT';
  attrs: Attribute[]; locked: Record<string, string>;
  meta: Record<string, any>;
  onApply: (values: Record<string, any>, applied: Suggestion[]) => void;
  onAddListValue: (attr: Attribute, value: string) => Promise<string | null>;
}

type Phase = 'idle' | 'reading' | 'filling' | 'done' | 'error';

export function OcrFill({ status, fileId, isNewFile, typeId, direction, attrs, locked, meta, onApply, onAddListValue, withTitle }: OcrFillProps & { withTitle?: boolean }) {
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>('idle');
  const [ocr, setOcr] = useState<OcrResult | null>(null);
  const [res, setRes] = useState<ExtractResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showText, setShowText] = useState<string | null>(null);
  const [pending, setPending] = useState<Suggestion[]>([]);
  const ran = useRef<string | null>(null);
  const metaRef = useRef(meta); metaRef.current = meta;

  const empty = (v: any) => v == null || v === '' || (Array.isArray(v) && v.length === 0);

  const run = async (auto: boolean) => {
    if (!fileId || !status?.enabled) return;
    setErr(null); setRes(null); setPending([]);
    try {
      setPhase('reading');
      const r = await waitOcr(fileId, true, setOcr);
      setOcr(r);
      if (r.status !== 'DONE') { setPhase('error'); setErr(r.status === 'SKIPPED' ? (r.error || 'Tipul de fișier nu poate fi citit') : r.status === 'FAILED' ? (r.error || 'Recunoașterea a eșuat') : 'Recunoașterea durează mai mult — reîncercați peste câteva momente'); return; }
      if (auto && !status.autofill) { setPhase('done'); return; }
      setPhase('filling');
      const fields = ocrFields(attrs, locked, !!withTitle);
      const x = await api.post<ExtractResult>('/api/ocr/extract', { fileId, typeId, direction, fields, ai: true });
      setRes(x);
      // fill only empty fields; the rest become suggestions the user can accept
      const values: Record<string, any> = {}; const applied: Suggestion[] = []; const rest: Suggestion[] = [];
      for (const s of x.suggestions) {
        const cur = metaRef.current[s.code];
        if (s.code === TITLE_FIELD.code) { values[s.code] = s.value; continue; }
        if (s.newListValue) { rest.push(s); continue; }
        if (empty(cur)) { values[s.code] = s.value; applied.push(s); }
        else if (String(cur) !== String(s.value)) rest.push(s);
      }
      if (applied.length || values[TITLE_FIELD.code]) onApply(values, applied);
      setPending(rest);
      setPhase('done');
      if (x.ai.error && status.ai.enabled) toast('Gemma nu este disponibil acum — s-au folosit regulile de completare', 'info');
    } catch (e: any) { setPhase('error'); setErr(e.message); }
  };

  useEffect(() => {
    if (!fileId || !status?.enabled || !isNewFile || !status.auto) return;
    if (ran.current === fileId) return;
    ran.current = fileId;
    run(true);
  }, [fileId, status?.enabled, isNewFile]);

  if (!status || !status.enabled || !fileId) return null;

  const apply = async (s: Suggestion) => {
    let value: any = s.value;
    if (s.newListValue) {
      const a = attrs.find(x => x.code === s.code);
      if (!a) return;
      const v = await onAddListValue(a, s.value);
      if (v == null) return;
      value = v;
    }
    onApply({ [s.code]: value }, [{ ...s, value }]);
    setPending(p => p.filter(x => x !== s));
  };
  const openText = async () => {
    try { const r = await api.get<OcrResult>(`/api/ocr/files/${fileId}`); setOcr(r); setShowText(r.text || ''); } catch (e: any) { toast(e.message, 'error'); }
  };

  const filled = res ? res.suggestions.length - pending.length : 0;
  const aiUsed = res?.ai.used;
  return (
    <div className={'ocr-strip ' + phase}>
      <div className="ocr-row">
        <span className="ocr-ico">{phase === 'reading' || phase === 'filling' ? <span className="spinner" /> : <Icon name={phase === 'error' ? 'alert' : aiUsed ? 'sparkles' : 'scan'} size={16} />}</span>
        <div className="grow">
          {phase === 'idle' && <><b>Completare din document</b><div className="muted small">Citește documentul atașat (OCR) și completează câmpurile goale{status.ai.enabled ? ` — cu ${status.ai.model || 'Gemma'}` : ' după regulile definite'}.</div></>}
          {phase === 'reading' && <><b>Se citește documentul…</b><div className="muted small">{ocr?.status === 'QUEUED' ? 'În așteptare' : 'Recunoaștere text (OCR)'} — puteți completa alte câmpuri între timp.</div></>}
          {phase === 'filling' && <><b>Se completează câmpurile…</b><div className="muted small">{status.ai.enabled ? `Reguli + ${status.ai.model || 'Gemma'} (poate dura câteva secunde)` : 'Aplicarea regulilor de completare'}</div></>}
          {phase === 'done' && (
            <>
              <b>{res ? (filled ? `${filled} ${filled === 1 ? 'câmp completat' : 'câmpuri completate'} din document` : 'Nu s-au găsit valori noi pentru câmpurile goale') : 'Text recunoscut'}</b>
              <div className="muted small">
                {ocr && <>{methodLabel(ocr.method)}{ocr.pages ? ` · ${ocr.pages} ${ocr.pages === 1 ? 'pagină' : 'pagini'}` : ''}{ocr.confidence != null ? ` · încredere ${Math.round(ocr.confidence)}%` : ''}</>}
                {res && <> · {aiUsed ? <span className="ai-used"><Icon name="sparkles" size={11} />{res.ai.model}</span> : 'reguli'}{res.ai.error && status.ai.enabled ? <span className="warn-text" title={res.ai.error}> · Gemma indisponibil, s-au folosit regulile</span> : null}</>}
                {res && res.suggestions.some(s => s.confidence === 'CHECK') && <> · <span className="warn-text">verificați câmpurile marcate cu galben</span></>}
              </div>
            </>
          )}
          {phase === 'error' && <><b>Documentul nu a putut fi citit</b><div className="muted small">{err}</div></>}
        </div>
        {(phase === 'done' || phase === 'error' || phase === 'idle') && (
          <div className="row" style={{ gap: 6 }}>
            {ocr?.status === 'DONE' && <button type="button" className="btn sm ghost" onClick={openText}><Icon name="eye" />Textul</button>}
            <button type="button" className="btn sm" onClick={() => run(false)}><Icon name="wand" />{phase === 'idle' ? 'Completează din document' : 'Completează din nou'}</button>
          </div>
        )}
      </div>
      {pending.length > 0 && (
        <div className="ocr-pending">
          <span className="muted small">Valori găsite în document, diferite de cele completate:</span>
          {pending.map((s, i) => (
            <div key={i} className="ocr-sugg">
              <b>{s.field}</b><span className="v" title={s.snippet}>{s.display}</span>
              <span className={'ocr-badge ' + s.source + (s.confidence === 'CHECK' ? ' check' : '')}>{SOURCE_LABEL[s.source]}</span>
              <span className="grow" />
              <button type="button" className="btn sm ghost" onClick={() => apply(s)}>{s.newListValue ? 'Adaugă în listă și aplică' : 'Aplică'}</button>
            </div>
          ))}
        </div>
      )}
      {showText != null && <OcrTextModal text={showText} suggestions={res?.suggestions} result={ocr} onClose={() => setShowText(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------- drawer tab
export function OcrPanel({ fileId, canWrite, running, error, onOcr }: { fileId: string | null | undefined; canWrite: boolean; running?: boolean; error?: string | null; onOcr?: () => void }) {
  const status = useOcrStatus();
  const [r, setR] = useState<OcrResult | null>(null);
  const [q, setQ] = useState('');
  const load = () => fileId ? api.get<OcrResult>(`/api/ocr/files/${fileId}`).then(setR).catch(() => setR(null)) : Promise.resolve();
  useEffect(() => { setR(null); load(); }, [fileId]);
  useEffect(() => { if (!running) load(); }, [running]);
  if (!fileId) return <p className="muted">Documentul nu are fișier atașat.</p>;
  if (!status) return <p className="muted">Se încarcă…</p>;
  const button = status.enabled && canWrite && onOcr
    ? <button className="btn sm primary" disabled={running} onClick={onOcr}>{running ? <><span className="spinner sm" />OCR în lucru…</> : <><Icon name="scan" />OCR — versiune nouă cu text</>}</button> : null;
  const failed = error ? <div className="banner small warn"><Icon name="alert" size={15} /><span>{error}</span></div> : null;
  if (!r || r.status === 'NONE' || r.status === 'QUEUED' || r.status === 'RUNNING') return (
    <div className="stack" style={{ gap: 10 }}>
      <p className="muted">{running || r?.status === 'QUEUED' || r?.status === 'RUNNING' ? 'Se recunoaște textul… La final se creează automat o versiune nouă a documentului, cu PDF căutabil.'
        : status.enabled ? 'Textul acestui document nu a fost încă recunoscut. Apăsați OCR: se creează o versiune nouă a documentului, cu PDF căutabil.'
        : status.installed ? 'OCR este dezactivat din Administrare → OCR.' : 'OCR (Tesseract) nu este instalat pe server.'}</p>
      {failed}
      {button && <div>{button}</div>}
    </div>
  );
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span className={'ocr-state ' + r.status}>{r.status === 'DONE' ? 'Text recunoscut' : r.status === 'FAILED' ? 'Eșuat' : 'Nu se poate citi'}</span>
        <OcrMeta r={r} />
        <span className="grow" />
        {button}
      </div>
      {failed}
      {r.error && <div className="banner small warn"><Icon name="alert" size={15} /><span>{r.error}</span></div>}
      {r.status === 'DONE' && (r.text ? (
        <>
          <div className="search"><Icon name="search" size={16} /><input className="input" type="search" autoComplete="off" placeholder="Caută în textul documentului…" value={q} onChange={e => setQ(e.target.value)} /></div>
          <HighlightedText text={r.text} query={q} />
        </>
      ) : <p className="muted">Nu s-a găsit text în document.</p>)}
    </div>
  );
}
